import type { ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "../../lib/cn";
import { Card } from "./Card";
import type { BadgeTone } from "./Badge";

export interface StatCardProps {
  label: string;
  /** Widened from string|number to ReactNode so an animated counter can be passed in.
   *  Every existing caller passing a string or number still type-checks unchanged. */
  value: ReactNode;
  delta?: number;
  icon?: ReactNode;
  tone?: Extract<BadgeTone, "accent" | "success" | "warn" | "danger" | "neutral">;
  className?: string;
}

const iconToneClasses: Record<NonNullable<StatCardProps["tone"]>, string> = {
  accent: "bg-accent-soft text-accent",
  success: "bg-success-soft text-success",
  warn: "bg-warn-soft text-warn",
  danger: "bg-danger-soft text-danger",
  neutral: "bg-surface-hover text-text-muted",
};

export function StatCard({ label, value, delta, icon, tone = "neutral", className }: StatCardProps) {
  const deltaPositive = typeof delta === "number" && delta >= 0;
  return (
    <Card className={cn("group/stat flex items-center gap-4 transition-colors duration-fast ease-standard hover:border-border/80", className)}>
      {icon && (
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-card",
            "ring-1 ring-inset ring-current/10",
            iconToneClasses[tone],
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-1">
        {/* Label above value, small and letterspaced: the eye lands on the number first. */}
        <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</p>
        <div className="flex items-baseline gap-2">
          <p className="tnum text-3xl font-semibold leading-none tracking-tight text-text">{value}</p>
          {typeof delta === "number" && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-xs font-medium",
                deltaPositive ? "text-success" : "text-danger",
              )}
            >
              {deltaPositive ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
              {Math.abs(delta)}%
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
